import { AbsoluteFill } from 'remotion'
import QuotesSvg from '../assets/QuotesSvg'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { BarsVisualization } from '../components/waveform/BarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

function BigTitleLayout({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, lp, sp, sl } = useOrientation(orientation)

  return (
    <AbsoluteFill className={cn(l && 'p-[80px]', sp && 'p-[60px]')}>
      <div
        className={cn(
          'flex justify-between',
          p && 'mb-[85px]',
          s && 'mb-[44px]',
          l && 'mb-[65px]',
        )}
      >
        <p
          style={style.subtitle}
          className="line-clamp-1 text-left text-[40px] leading-[126%] font-[500]"
        >
          {clipScene.subtitle}
        </p>

        {clipScene.showZealousLogo && (
          <ZealousSvg
            color={style.logo.color}
            className={cn('h-[49px] opacity-50', l && 'hidden')}
          />
        )}
      </div>

      <p
        style={style.title}
        className={cn(
          'shrink-0 text-left leading-[130%] font-[600]',
          p && 'line-clamp-3 text-[100px]',
          s && 'line-clamp-2 text-[82px]',
          l && 'line-clamp-1 text-[90px]',
        )}
      >
        {clipScene.title}
      </p>

      <div className="flex h-full w-full max-w-[1000px] items-center">
        <BarsVisualization
          className="w-full"
          color={style.waveform.color}
          gapSize={10}
          height={160}
          lineThickness={13}
          mediaStart={clipScene.mediaStart}
          mediaUrl={sceneAudioSrc(clipScene)}
          waveformUrl={clipScene.waveformUrl}
          roundness={6}
        />
      </div>

      <div
        className={cn(p && 'mb-[159px]', s && 'mb-[90px]', l && 'mb-[62px]')}
      >
        {children}
      </div>

      <div className="flex items-center">
        <div
          className={cn(
            'flex w-full',
            s && 'space-x-[40px]',
            lp && 'space-x-[51px]',
          )}
        >
          <QuotesSvg
            color={style.border.color}
            className={cn('shrink-0', s && 'h-[62px]', lp && 'h-[74px]')}
          />

          <p
            style={style.speakerUsername}
            className={cn(
              'w-full text-left font-[700]',
              s && 'text-[38px]',
              lp && 'text-[46px]',
            )}
          >
            {speaker?.username}
          </p>
        </div>

        <FallbackImg
          src={speaker?.avatarUrl}
          className={cn(
            'aspect-square shrink-0 rounded-full object-cover',
            p && 'h-[130px]',
            sl && 'h-[100px]',
          )}
        />
      </div>
    </AbsoluteFill>
  )
}

export const bigTitleLayoutInfo: LayoutInfo = {
  name: 'bigTitle',
  Component: BigTitleLayout,
  defaultScene: {
    ...defaultClipScene,
    layout: 'bigTitle',
  },
  defaultStyle: {
    background: { color: 'primary' },
    border: { color: 'accent' },
    logo: { color: 'white' },
    waveform: { color: 'accent' },
    subtitle: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    title: {
      color: 'secondary',
      fontFamily: 'primary',
    },
    speakerName: { color: 'secondary', fontFamily: 'primary' },
    speakerUsername: { color: 'secondary', fontFamily: 'primary' },
    transcription: {
      highlightColor: 'accent',
      color: 'secondary',
      unPlayedColor: 'secondary',
      unPlayedOpacity: 0.5,
      fontFamily: 'primary',
      highlightType: 'last-word',
      scrollType: 'line-by-line',
      fontSize: 46,
      fontWeight: 500,
      linesPerPage: 3,
      lineHeight: 1.68,
      textAlign: 'left',
    },
  },
  transcriptions: {
    square: {},
    landscape: {
      fontSize: 52,
      lineHeight: 1.7,
      linesPerPage: 3,
    },
    portrait: {
      fontSize: 63,
      lineHeight: 1.88,
      linesPerPage: 6,
    },
  },
}
